/**
 * Pure helper for the Shopify parent/variant pre-pass in BulkImport.
 *
 * Groups the preview items that will actually be imported, by Shopify product
 * id, so the import loop can upsert one parent reference per multi-variant
 * product before writing the variants. The CRITICAL property pinned by its
 * tests is that unticked rows (`included === false`) are excluded from every
 * group — if they aren't, the pre-pass creates parent docs for products whose
 * variants will all be skipped, polluting `variantReferences` and the
 * added/updated tallies the admin sees after the run.
 *
 * `shouldWritePreviewItem` from `importGate.ts` is the authoritative "will this
 * row be imported" predicate (its sole rule is `item.included`), so this
 * helper calls it rather than reading `item.included` directly: the gate and
 * the pre-pass stay in lock-step by construction.
 *
 * Kept standalone (no React, no Firestore) so the rule is unit-testable the
 * same way `importGate.test.ts` pins `shouldWritePreviewItem`.
 */
import { shouldWritePreviewItem } from './importGate';
import { variantGroupOf, type StoreIdentifiedEntry } from './storeDocIds';

export interface GroupableItem {
  entry: StoreIdentifiedEntry;
  included: boolean;
}

/**
 * Build the group-key → item-index groups for the parent pre-pass (Shopify:
 * product id; WooCommerce / Square Online: the product-level doc id).
 *
 * Only indices whose row would actually be written (ticked, per
 * `shouldWritePreviewItem`) are included. Products whose only ticked rows
 * were unticked by the admin never appear in the output at all — no parent
 * doc is created for them.
 *
 * Keeps the `shopifyVariantCount >= 2` gate (a product with a single Shopify
 * variant is a standalone ref, not a parent — unchanged from the inline logic
 * this replaced).
 */
export function groupShopifyVariantsForImport(
  items: readonly GroupableItem[],
): Map<string, number[]> {
  const groups = new Map<string, number[]>();
  items.forEach((item, idx) => {
    if (!shouldWritePreviewItem(item)) return;
    // Shopify, WooCommerce and Square Online variants alike (variantGroupOf);
    // a Shopify group's key is still its bare product id.
    const group = variantGroupOf(item.entry);
    if (!group) return;
    const arr = groups.get(group.key) ?? [];
    arr.push(idx);
    groups.set(group.key, arr);
  });
  return groups;
}
